<template>
  <div class="w-full">
    <div class="flex flex-col min-w-[16rem] w-full">
      <div class="relative focus-within:z-10">
        <s-input
          v-model="filterText"
          :placeholder="t('labels.filterOptions')"
          :aria-label="t('labels.filterOptions')"
          :class="['w-full !rounded-b-none pr-8', embedded && '!rounded-t-none']"
          ref="searchInput"
          @keydown.enter.prevent="selectFirstFilteredOption"
          @keydown="handleSearchInputKeydown"
        />
        <s-icon
          name="magnify"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 transition ease-out duration-100 mt-px"
          :class="[filterText ? 'opacity-0' : 'opacity-100']"
          aria-hidden="true"
        />
      </div>
      <span role="status" class="sr-only">{{ filterResultAnnouncement }}</span>
      <ul
        ref="listRef"
        class="max-h-60 overflow-auto border border-t-0 border-gray-300 scroll-shadow rounded-b-lg pt-px"
      >
        <li
          v-if="filteredOptions.length === 0"
          class="py-2 px-4 text-left bg-gray-100 text-gray-600 italic whitespace-nowrap"
        >
          {{ $t('filter.noResults') }}
        </li>
        <li
          v-else
          v-for="(option, index) in filteredOptions"
          :key="option.value"
          class="-mb-px first:-mt-px"
        >
          <button
            :data-option-index="index"
            class="w-full text-left transition-colors ease-out duration-100 py-1.5 px-3 hover:bg-blue-200/20 focus-visible:bg-blue-200/20 cursor-pointer select-none outline-transparent focus-visible:outline-blue-400 -outline-offset-1"
            :class="[isSelected(option) ? 'bg-blue-200/20' : '']"
            :aria-current="isSelected(option) || undefined"
            @click="selectOption(option)"
            @keydown="handleOptionKeydown($event, option, index)"
          >
            <slot name="option" v-bind="{option}">
              <span class="block truncate">{{ option.label }}</span>
            </slot>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends Option">
import {computed, ref, watch, onMounted} from 'vue';
import {useDebounceFn} from '@vueuse/core';
import {useI18n} from 'vue-i18n';
import SIcon from './SIcon.vue';
import SInput from './SInput.vue';
import type {Option} from './controls/Option';

const {
  disabled = false,
  options,
  embedded = false,
  autofocus = false,
} = defineProps<{
  options: T[];
  disabled?: boolean;
  /** When true, removes top rounding (e.g. when shown under a header in SFilterMenu) */
  embedded?: boolean;
  /** When true, focuses the search input on mount */
  autofocus?: boolean;
}>();

const model = defineModel<T['value'] | null>();
const emit = defineEmits(['close']);

const filterText = ref('');
const selectedOption = ref<T | null>(null);
const searchInput = ref<InstanceType<typeof SInput> | null>(null);
const listRef = ref<HTMLElement | null>(null);

onMounted(() => {
  if (autofocus) {
    const el = (searchInput.value as any)?.$el;
    if (el && typeof el.focus === 'function') {
      setTimeout(() => el.focus(), 100);
    }
  }
});

// Update selected option when modelValue changes
watch(
  model,
  (newValue) => {
    selectedOption.value = options.find((option) => option.value === newValue) || null;
  },
  {immediate: true}
);

// Filtered options based on search text
const filteredOptions = computed(() => {
  if (!filterText.value) return options;

  return options.filter((option) => {
    const label = option.label.toLowerCase();
    return label.includes(filterText.value.toLowerCase());
  });
});

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const TYPING_PAUSE_BEFORE_ANNOUNCING_MS = 500;

const filterResultAnnouncement = ref('');

const announceFilterResults = useDebounceFn(() => {
  if (!filterText.value) {
    filterResultAnnouncement.value = '';
  } else if (filteredOptions.value.length === 0) {
    filterResultAnnouncement.value = t('announcements.noResults');
  } else {
    filterResultAnnouncement.value = t('announcements.resultCount', filteredOptions.value.length);
  }
}, TYPING_PAUSE_BEFORE_ANNOUNCING_MS);

watch(filterText, announceFilterResults);

function isSelected(option: T): boolean {
  return selectedOption.value && selectedOption.value.value === option.value;
}

// Select an option and close the dialog
function selectOption(option: T): void {
  if (disabled) return;

  selectedOption.value = option;
  model.value = option.value;
  emit('close');
}

// Lets Enter pick the top search result without arrowing/tabbing down to it first
function selectFirstFilteredOption(): void {
  const option = filteredOptions.value[0];
  if (option) selectOption(option);
}

function focusOptionAtIndex(index: number): void {
  const element = listRef.value?.querySelector<HTMLElement>(`[data-option-index="${index}"]`);
  element?.focus({preventScroll: true});
}

function handleSearchInputKeydown(event: KeyboardEvent): void {
  if (event.key !== 'ArrowDown' || filteredOptions.value.length === 0) return;

  event.preventDefault();
  event.stopPropagation();
  focusOptionAtIndex(0);
}

/** Arrow keys step between options; the ends are left unhandled rather than wrapping around. */
function handleOptionKeydown(event: KeyboardEvent, option: T, index: number): void {
  const count = filteredOptions.value.length;

  if (event.key === 'Enter') {
    event.preventDefault();
    selectOption(option);
  } else if (event.key === 'ArrowDown' && index < count - 1) {
    event.preventDefault();
    event.stopPropagation();
    focusOptionAtIndex(index + 1);
  } else if (event.key === 'ArrowUp' && index > 0) {
    event.preventDefault();
    event.stopPropagation();
    focusOptionAtIndex(index - 1);
  }
}
</script>
<i18n>
{
  "en": {
    "labels.filterOptions": "Filter Options",
    "announcements.noResults": "No results found.",
    "announcements.resultCount": "{count} result | {count} results"
  },
  "fr": {
    "labels.filterOptions": "Filtrer les options",
    "announcements.noResults": "Aucun résultat trouvé.",
    "announcements.resultCount": "{count} résultat | {count} résultats"
  }
}
</i18n>
